import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { DispositivosService } from '../services/dispositivos.service';
import { Dispositivo, MotivoCierre, NOTA_MAX, RespuestaCierre } from '../types/dispositivos.types';
import { MOTIVOS, nombreEquipo, rolLegible } from '../utils/dispositivos';

interface Props {
  /** `null` = cerrado. */
  dispositivo: Dispositivo | null;
  /** El código visible (4 o 6 caracteres). */
  codigo: string;
  onHide: () => void;
  /** La sesión se cerró: la pantalla vuelve a leer la lista. */
  onCerrado: (r: RespuestaCierre) => void;
  /** Botón «Ir a Usuarios» del paso siguiente (cambiar la contraseña). */
  onIrAUsuarios: () => void;
}

/**
 * «Cerrar sesión de este equipo» (sólo TI; D12).
 *
 * El motivo es obligatorio (`perdido | robado | baja | otro`) y la nota,
 * opcional, de hasta 200 caracteres. Al terminar dice cuántas conexiones
 * abiertas se cortaron y recuerda el paso que falta del procedimiento (§12):
 * cerrar la sesión de UN equipo no cambia la contraseña de la cuenta, y quien
 * la conozca puede volver a entrar desde otro equipo (o desde éste, con los
 * datos del navegador borrados).
 */
export const CerrarSesionDialog: React.FC<Props> = ({ dispositivo, codigo, onHide, onCerrado, onIrAUsuarios }) => {
  const [motivo, setMotivo] = useState<MotivoCierre | null>(null);
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<RespuestaCierre | null>(null);

  // Cada equipo empieza en limpio: un motivo elegido para otro no se arrastra.
  useEffect(() => {
    setMotivo(null);
    setNota('');
    setEnviando(false);
    setError(null);
    setResultado(null);
  }, [dispositivo?.id]);

  if (!dispositivo) return null;
  const nombre = nombreEquipo(codigo);
  const cuenta = dispositivo.cuenta?.email ?? '—';

  const confirmar = async () => {
    if (!motivo || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await DispositivosService.cerrarSesion(dispositivo.id, { motivo, nota: nota.slice(0, NOTA_MAX) });
      setResultado(r);
      onCerrado(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cerrar la sesión del equipo.');
    } finally {
      setEnviando(false);
    }
  };

  const pie = resultado ? (
    <div className="disp-dialogo-pie">
      <Button label="Ir a Usuarios" icon="pi pi-users" className="p-button-outlined" onClick={onIrAUsuarios} />
      <Button label="Entendido" onClick={onHide} autoFocus />
    </div>
  ) : (
    <div className="disp-dialogo-pie">
      <Button label="Cancelar" className="p-button-text" onClick={onHide} disabled={enviando} />
      <Button
        label="Cerrar sesión del equipo"
        icon="pi pi-sign-out"
        className="p-button-danger"
        onClick={() => void confirmar()}
        disabled={!motivo}
        loading={enviando}
      />
    </div>
  );

  return (
    <Dialog
      header={resultado ? `Sesión cerrada · ${nombre}` : `Cerrar sesión de ${nombre}`}
      visible
      onHide={onHide}
      className="disp-overlay"
      style={{ width: '560px', maxWidth: 'calc(100vw - 32px)' }}
      footer={pie}
      closable={!enviando}
    >
      {resultado ? (
        <div className="disp-cierre-resultado" data-testid="cierre-resultado">
          <p>
            <strong>{nombre}</strong> ya no puede usar la app ni volver a entrar con ninguna cuenta hasta
            que TI lo reactive.
          </p>
          <p data-testid="sockets-cerrados">
            {resultado.socketsCerrados > 0
              ? `Se cortaron ${resultado.socketsCerrados === 1 ? '1 conexión abierta' : `${resultado.socketsCerrados} conexiones abiertas`}: si alguien lo tiene en la mano, ya está viendo el aviso.`
              : 'No tenía ninguna conexión abierta: quedará fuera en cuanto intente usar la app.'}
          </p>
          <Message
            severity="warn"
            className="disp-mensaje-ancho"
            content={
              <div>
                <strong>Falta un paso: cambia la contraseña de {cuenta}</strong> en Usuarios y dásela al
                gerente. Los demás equipos de la cuenta siguen trabajando y usarán la contraseña nueva en su
                próximo inicio de sesión (a más tardar en 7 días). Durante los días siguientes, vigila si
                aparece un equipo «Nuevo» en esta cuenta.
              </div>
            }
          />
        </div>
      ) : (
        <div className="disp-cierre-form">
          <p className="disp-cierre-equipo">
            <strong>{nombre}</strong>
            {dispositivo.etiqueta ? ` · ${dispositivo.etiqueta}` : ''}
            <br />
            <span className="disp-tenue">
              {cuenta} · {rolLegible(dispositivo.cuenta?.rol)}
              {dispositivo.ultimoVendedor?.nombre ? ` · último vendedor: ${dispositivo.ultimoVendedor.nombre}` : ''}
            </span>
          </p>

          <fieldset className="disp-motivos">
            <legend>
              Motivo <span className="disp-requerido">(obligatorio)</span>
            </legend>
            {MOTIVOS.map((m) => (
              <div key={m.valor} className="disp-motivo">
                <RadioButton
                  inputId={`motivo-${m.valor}`}
                  name="motivo-cierre"
                  value={m.valor}
                  checked={motivo === m.valor}
                  onChange={(e) => setMotivo(e.value as MotivoCierre)}
                  disabled={enviando}
                />
                <label htmlFor={`motivo-${m.valor}`}>{m.etiqueta}</label>
              </div>
            ))}
          </fieldset>
          {!motivo && <small className="disp-tenue">Elige el motivo para poder cerrar la sesión.</small>}

          <label htmlFor="nota-cierre" className="disp-campo-etiqueta">
            Nota (opcional)
          </label>
          <InputTextarea
            id="nota-cierre"
            value={nota}
            maxLength={NOTA_MAX}
            onChange={(e) => setNota(e.target.value.slice(0, NOTA_MAX))}
            rows={3}
            autoResize
            className="w-full"
            placeholder="Ej.: se perdió el 10-oct en piso; la tienda lo buscó sin éxito"
            disabled={enviando}
          />
          <small className="disp-tenue">
            {nota.length}/{NOTA_MAX}
          </small>

          <Message
            severity="info"
            className="disp-mensaje-ancho"
            text="Los demás equipos de esta cuenta siguen trabajando. Para que no puedan volver a entrar con otro equipo, cambia también la contraseña en Usuarios."
          />

          {error && (
            <Message severity="error" className="disp-mensaje-ancho" text={error} />
          )}
        </div>
      )}
    </Dialog>
  );
};
